import { EmptyArray } from "../domain/empty/model";
import { ReceiptRepositoryImplementation } from '../infrastructure/receipts-repository-implementation';
import { Receipt, ReceiptType } from '../domain/receipts/model';


import { cacheManager } from '../infrastructure/cache-manager';

const repo = new ReceiptRepositoryImplementation('', null); // uid and cid should be provided by context

const RECEIPTS_PREFIX = 'receipts';
const RECEIPT_TYPES_PREFIX = 'receipt_types';

// Receipt Use Cases
async function createReceipt(data: Partial<Receipt>) {
  try {
    const result = await repo.createReceipt(data).toPromise();
    cacheManager.invalidate(RECEIPTS_PREFIX);
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getReceipt(id: number) {
  try {
    const cacheKey = `${RECEIPTS_PREFIX}-detail-${id}`;
    const cached = cacheManager.getItem(cacheKey);

    if (cached) {
      console.log(`Returning receipt with ID ${id} from cache`);
      return cached;
    }
    const result = await repo.getReceipt(id).toPromise();
    const data = result.data;
    if (!data) return EmptyArray;

    cacheManager.setItem(cacheKey, data);
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getReceipts(page: number, limit: number) {
  try {
    const cacheKey = `${RECEIPTS_PREFIX}-list-${page}-${limit}`;
    const cached = cacheManager.getItem(cacheKey);

    if (cached) {
      console.log(`Returning receipts for page ${page} from cache`);
      return cached;
    }
    const result = await repo.getReceipts(page, limit).toPromise();
    const data = result.data;
    if (data.length === 0) return EmptyArray;

    cacheManager.setItem(cacheKey, data);
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function updateReceipt(id: number, data: Partial<Receipt>) {
  try {
    const result = await repo.updateReceipt(id, data).toPromise();
    cacheManager.invalidate(RECEIPTS_PREFIX);
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function deleteReceipt(id: number) {
  try {
    await repo.deleteReceipt(id).toPromise();
    cacheManager.invalidate(RECEIPTS_PREFIX);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// ReceiptType Use Cases
async function createReceiptType(data: Partial<ReceiptType>) {
  try {
    const result = await repo.createReceiptType(data).toPromise();
    cacheManager.invalidate(RECEIPT_TYPES_PREFIX);
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getReceiptType(id: number) {
  try {
    const cacheKey = `${RECEIPT_TYPES_PREFIX}-detail-${id}`;
    const cached = cacheManager.getItem(cacheKey);

    if (cached) {
      console.log(`Returning receipt type with ID ${id} from cache`);
      return cached;
    }

    const result = await repo.getReceiptType(id).toPromise();
    const data = result.data;
    if (!data) return EmptyArray;

    cacheManager.setItem(cacheKey, data);
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getReceiptTypes(page: number, limit: number) {
  try {
    const cacheKey = `${RECEIPT_TYPES_PREFIX}-list-${page}-${limit}`;
    const cached = cacheManager.getItem(cacheKey);

    if (cached) {
      console.log(`Returning receipt types for page ${page} from cache`);
      return cached;
    }

    const result = await repo.getReceiptTypes(page, limit).toPromise();
    const data = result.data;
    if (data.length === 0) return EmptyArray;

    cacheManager.setItem(cacheKey, data);
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function updateReceiptType(id: number, data: Partial<ReceiptType>) {
  try {
    const result = await repo.updateReceiptType(id, data).toPromise();
    cacheManager.invalidate(RECEIPT_TYPES_PREFIX);
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function deleteReceiptType(id: number) {
  try {
    await repo.deleteReceiptType(id).toPromise();
    cacheManager.invalidate(RECEIPT_TYPES_PREFIX);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export {
  createReceipt,
  getReceipt,
  getReceipts,
  updateReceipt,
  deleteReceipt,
  createReceiptType,
  getReceiptType,
  getReceiptTypes,
  updateReceiptType,
  deleteReceiptType,
};
